import { IChartApi } from 'lightweight-charts'
import { BitmapPositionLength, DrawingTypes } from './drawing.interface'
import { drawingClassMap } from './drawing.constants'

/**
 * Determines the bitmap position and length for a dimension of a shape to be drawn.
 * @param position1Media - media coordinate for the first point
 * @param position2Media - media coordinate for the second point
 * @param pixelRatio - pixel ratio for the corresponding axis (vertical or horizontal)
 * @returns Position of of the start point and length dimension.
 */
export function positionsBox(
    position1Media: number,
    position2Media: number,
    pixelRatio: number
): BitmapPositionLength {
    const scaledPosition1 = Math.round(pixelRatio * position1Media)
    const scaledPosition2 = Math.round(pixelRatio * position2Media)
    return {
        position: Math.min(scaledPosition1, scaledPosition2),
        length: Math.abs(scaledPosition2 - scaledPosition1) + 1
    }
}

export function scaledCoordinate(coordinate: number, pixelRatio: number) {
    return Math.round(coordinate * pixelRatio)
}

export function determineChartX(
    chart: IChartApi,
    chartBounds: DOMRect,
    mouseX: number
): number | null {
    const x = mouseX - chartBounds.left - chart.priceScale('left').width()
    if (x < 0 || x > chart.timeScale().width()) return null
    return x
}

export function determineChartY(
    chart: IChartApi,
    chartBounds: DOMRect,
    mouseY: number
): number | null {
    const y = mouseY - chartBounds.top
    if (y < 0 || y > chartBounds.height) return null
    const ts = chart.timeScale().height()
    const clippedMax = chartBounds.height - ts
    const maxY = clippedMax >= chartBounds.height * 0.5 ? clippedMax : chartBounds.height
    if (y > maxY) return null
    return y
}

export function getDrawingClass(type: DrawingTypes) {
    return drawingClassMap[type]
}

export function getDistanceFromLine(x: number, y: number, slope: number, intercept: number) {
    return Math.abs(y * (-1) + slope * x + intercept) / Math.sqrt(slope * slope + 1)
}

export function getDistanceBetweenPoints(x1: number, x2: number, y1: number, y2: number) {
    return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2))
}

export function calculateTriangleArea(x1: number, y1: number, x2: number, y2: number, x3: number, y3: number) {
    return Math.abs((x1 * (y2 - y3) + x2 * (y3 - y1) + x3 * (y1 - y2)) / 2.0)
}

export function isSegmentIntersecting(p1x: number, p1y: number, p2x: number, p2y: number, slope: number, intercept: number) {
    const d1 = p1y - slope * p1x - intercept
    const d2 = p2y - slope * p2x - intercept

    return d1 * d2 < 0
}